/**
 * SquadCup (Andrea 2026-10-08): la coppa fra i vincitori delle leghe.
 * Lettura per tutti, gestione per l'admin app.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuthStore } from "../lib/auth-store";

export interface SquadraBreve {
  id: string;
  nome: string;
  logo_data_url: string | null;
  lega_nome: string;
}

export interface SquadCupPartita {
  id: string;
  ordine: number;
  casa: SquadraBreve;
  trasferta: SquadraBreve;
  fp_casa_andata: string | null;
  fp_trasferta_andata: string | null;
  fp_casa_ritorno: string | null;
  fp_trasferta_ritorno: string | null;
  vincitore_squadra_id: string | null;
  calcolata_at: string | null;
}

export interface SquadCupTurno {
  id: string;
  numero: number;
  nome: string;
  giornata_sa_andata: number;
  giornata_sa_ritorno: number | null;
  partite: SquadCupPartita[];
}

export interface SquadCupEdizione {
  id: string;
  nome: string;
  stagione_serie_a: string;
  stato: "bozza" | "in_corso" | "conclusa";
  regola: "campioni" | "campioni_e_coppe";
  di_prova: boolean;
  n_partecipanti: number;
  vincitore: SquadraBreve | null;
  created_at: string;
}

export interface SquadCupDettaglio extends SquadCupEdizione {
  note: string | null;
  partecipanti: { squadra: SquadraBreve; titolo: string; seed: number }[];
  turni: SquadCupTurno[];
}

export interface SquadCupVinta {
  squadra_id: string;
  edizione_nome: string;
  stagione_serie_a: string;
}

export interface Candidato {
  lega_id: string;
  lega_nome: string;
  squadra_id: string;
  squadra_nome: string;
  logo_data_url: string | null;
  titolo: "campione" | "coppa";
  stagione_anno: string;
}

export interface CandidatiOut {
  n_leghe: number;
  min_squadre: number;
  regola_consigliata: "campioni" | "campioni_e_coppe";
  stagione_serie_a_corrente: string | null;
  candidati: Candidato[];
}

export interface GiornataSA {
  numero: number;
  prima_partita: string | null;
  ultima_partita: string | null;
}

const CHIAVE = ["squadcup"] as const;

export function useSquadCupEdizioni() {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: [...CHIAVE, "edizioni"],
    queryFn: async () => (await api.get<SquadCupEdizione[]>("/squadcup")).data,
    enabled: isAuth,
  });
}

export function useSquadCupEdizione(id: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: [...CHIAVE, "edizione", id],
    queryFn: async () => (await api.get<SquadCupDettaglio>(`/squadcup/${id}`)).data,
    enabled: isAuth && !!id,
  });
}

export function useSquadCupVinte() {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: [...CHIAVE, "vinte"],
    queryFn: async () => (await api.get<SquadCupVinta[]>("/squadcup/vinte")).data,
    enabled: isAuth,
    staleTime: 10 * 60 * 1000,
  });
}

// --- admin app ---
export function useSquadCupCandidati(attivo: boolean) {
  return useQuery({
    queryKey: [...CHIAVE, "candidati"],
    queryFn: async () => (await api.get<CandidatiOut>("/admin-app/squadcup/candidati")).data,
    enabled: attivo,
  });
}

export function useGiornateSA(stagioneSerieA: string | null) {
  return useQuery({
    queryKey: [...CHIAVE, "giornate-sa", stagioneSerieA],
    queryFn: async () =>
      (
        await api.get<GiornataSA[]>("/admin-app/squadcup/giornate-sa", {
          params: { stagione_serie_a: stagioneSerieA },
        })
      ).data,
    enabled: !!stagioneSerieA,
  });
}

function useInvalida() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: CHIAVE });
}

export function useCreaEdizione() {
  const invalida = useInvalida();
  return useMutation({
    mutationFn: async (payload: {
      nome: string;
      stagione_serie_a: string;
      regola: "campioni" | "campioni_e_coppe";
      di_prova: boolean;
      note?: string;
    }) => (await api.post<SquadCupDettaglio>("/admin-app/squadcup", payload)).data,
    onSuccess: invalida,
  });
}

export function useImpostaPartecipanti() {
  const invalida = useInvalida();
  return useMutation({
    mutationFn: async (args: {
      id: string;
      partecipanti: { squadra_id: string; titolo: string }[];
    }) =>
      (
        await api.put<SquadCupDettaglio>(`/admin-app/squadcup/${args.id}/partecipanti`, {
          partecipanti: args.partecipanti,
        })
      ).data,
    onSuccess: invalida,
  });
}

export function useSorteggio() {
  const invalida = useInvalida();
  return useMutation({
    mutationFn: async (args: {
      id: string;
      giornate: { andata: number; ritorno: number | null }[];
    }) =>
      (
        await api.post<SquadCupDettaglio>(`/admin-app/squadcup/${args.id}/sorteggio`, {
          giornate: args.giornate,
        })
      ).data,
    onSuccess: invalida,
  });
}

export function useCalcolaSquadCup() {
  const invalida = useInvalida();
  return useMutation({
    mutationFn: async (id: string) =>
      (
        await api.post<{ report: Record<string, number | boolean>; edizione: SquadCupDettaglio }>(
          `/admin-app/squadcup/${id}/calcola`,
        )
      ).data,
    onSuccess: invalida,
  });
}

export function useEliminaEdizione() {
  const invalida = useInvalida();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin-app/squadcup/${id}`);
    },
    onSuccess: invalida,
  });
}
